import React, { useState, useEffect, useMemo } from "react";
import {
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  FileText,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Clock,
  Download,
  Eye,
  CreditCard,
  RefreshCw,
} from "lucide-react";
import { format } from "date-fns";
import html2pdf from "html2pdf.js";
import { toast } from "react-hot-toast";
import apiService from "../services/api";

import LoadingSpinner from "../components/ui/LoadingSpinner";

// Simple Payment Modal Component
const PaymentModal = ({ invoice, isOpen, onClose, onPaymentSaved }) => {
  const [paymentData, setPaymentData] = useState({
    paymentDate: format(new Date(), "yyyy-MM-dd"),
    bank: "",
    paymentMode: "bank_transfer",
    paymentAmount: 0,
    referenceNumber: "",
    notes: "",
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (invoice && isOpen) {
      const totalAmount = invoice.totalAmount || invoice.amount || 0;
      const paidAmount =
        invoice.payments?.reduce((sum, p) => sum + p.amountPaid, 0) || 0;
      const remainingAmount = totalAmount - paidAmount;

      setPaymentData({
        paymentDate: format(new Date(), "yyyy-MM-dd"),
        bank: "",
        paymentMode: "bank_transfer",
        paymentAmount: remainingAmount,
        referenceNumber: "",
        notes: "",
      });
    }
  }, [invoice, isOpen]);

  const handleInputChange = (field, value) => {
    setPaymentData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getPaymentStatus = () => {
    const totalAmount = invoice.totalAmount || invoice.amount || 0;
    const currentPaid =
      invoice.payments?.reduce((sum, p) => sum + p.amountPaid, 0) || 0;
    const newPaidAmount =
      currentPaid + parseFloat(paymentData.paymentAmount || 0);

    if (newPaidAmount >= totalAmount) {
      return "paid";
    } else if (newPaidAmount > 0) {
      return "partially_paid";
    } else {
      return "unpaid";
    }
  };

  const handleSubmitPayment = async () => {
    setLoading(true);
    try {
      // Prepare payment data for backend API
      const paymentPayload = {
        amountPaid: parseFloat(paymentData.paymentAmount),
        method: paymentData.paymentMode,
        paymentDate: paymentData.paymentDate,
        bankName: paymentData.bank,
        paymentReference: paymentData.referenceNumber,
        notes: paymentData.notes,
        paymentStatus: "completed",
      };

      // Call the backend API to record payment and update invoice status
      const response = await apiService.post(
        `/invoices/${invoice.id}/payments`,
        paymentPayload
      );

      const newStatus = getPaymentStatus();
      toast.success(
        `Payment recorded successfully! Invoice status updated to: ${newStatus
          .replace("_", " ")
          .toUpperCase()}`
      );

      // Refresh the invoices list to show updated status
      onPaymentSaved && onPaymentSaved();
      onClose();
    } catch (error) {
      console.error("Error recording payment:", error);
      toast.error(error.response?.data?.error || "Failed to record payment");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const totalAmount = invoice?.totalAmount || invoice?.amount || 0;
  const paidAmount =
    invoice?.payments?.reduce((sum, p) => sum + p.amountPaid, 0) || 0;
  const remainingAmount = totalAmount - paidAmount;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>

        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-green-800 text-white p-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold flex items-center gap-2">
                💰 Record Payment
              </h2>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200"
              >
                <span className="text-xl">×</span>
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* Invoice Summary */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="font-semibold text-gray-800 mb-2">
                Invoice Details
              </h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Invoice:</span>
                  <span className="font-medium">
                    {invoice?.invoiceNumber || `INV-${invoice?.id}`}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Customer:</span>
                  <span className="font-medium">
                    {invoice?.order?.customer?.businessName}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Amount:</span>
                  <span className="font-medium">₹{totalAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Paid Amount:</span>
                  <span className="font-medium text-green-600">
                    ₹{paidAmount.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-gray-600 font-medium">Remaining:</span>
                  <span className="font-bold text-red-600">
                    ₹{remainingAmount.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Date
                </label>
                <input
                  type="date"
                  value={paymentData.paymentDate}
                  onChange={(e) =>
                    handleInputChange("paymentDate", e.target.value)
                  }
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Mode
                </label>
                <select
                  value={paymentData.paymentMode}
                  onChange={(e) =>
                    handleInputChange("paymentMode", e.target.value)
                  }
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                  <option value="upi">UPI</option>
                  <option value="cash">Cash</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="debit_card">Debit Card</option>
                  <option value="online_payment">Online Payment</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bank
                </label>
                <input
                  type="text"
                  value={paymentData.bank}
                  onChange={(e) => handleInputChange("bank", e.target.value)}
                  placeholder="Bank name (if applicable)"
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Amount
                </label>
                <input
                  type="number"
                  value={paymentData.paymentAmount}
                  onChange={(e) =>
                    handleInputChange("paymentAmount", e.target.value)
                  }
                  step="0.01"
                  min="0"
                  max={remainingAmount}
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reference Number
                </label>
                <input
                  type="text"
                  value={paymentData.referenceNumber}
                  onChange={(e) =>
                    handleInputChange("referenceNumber", e.target.value)
                  }
                  placeholder="Transaction/Cheque number"
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={paymentData.notes}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  rows="2"
                  placeholder="Additional notes (optional)"
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              {/* Payment Status Preview */}
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="text-sm text-blue-800">
                  <strong>Status after payment:</strong>
                  <span
                    className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                      getPaymentStatus() === "paid"
                        ? "bg-green-100 text-green-800"
                        : getPaymentStatus() === "partially_paid"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {getPaymentStatus().replace("_", " ").toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmitPayment}
              disabled={
                loading ||
                !paymentData.paymentAmount ||
                paymentData.paymentAmount <= 0
              }
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Processing...
                </>
              ) : (
                "Record Payment"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Professional Invoice Modal Component
const ProfessionalInvoiceModal = ({ invoice, isOpen, onClose, onDownload }) => {
  if (!invoice) return null;

  const companyInfo = {
    name: "Axia B2B Solutions",
    address: "123 Business Park, Suite 100",
    city: "Tech City, State 12345",
    state: "State",
    zipCode: "12345",
    country: "India",
    phone: "+91 98765 43210",
    email: "sales@ennovaceb2b.com",
    website: "www.ennovaceb2b.com",
    gstin: "27ABCDE1234F1Z5",
    pan: "ABCDE1234F",
    logo: "/logo192.png",
    bankDetails: {
      bankName: "HDFC Bank",
      accountName: "Axia B2B Solutions",
      accountNumber: "1234567890123456",
      ifscCode: "HDFC0001234",
      branch: "Tech City Branch",
      accountType: "Current Account",
    },
  };

  const customer = invoice.order?.customer || {};
  const orderItems = invoice.items || invoice.order?.orderItems || [];
  const invoiceNumber = invoice.invoiceNumber || `INV-${invoice.id}`;
  const invoiceDate = invoice.invoiceDate
    ? new Date(invoice.invoiceDate)
    : new Date(invoice.createdAt);
  const dueDate = new Date(invoice.dueDate);

  // Calculate totals
  const subtotal =
    invoice.subtotal ||
    orderItems.reduce(
      (sum, item) => sum + (item.price || item.unitPrice || 0) * item.quantity,
      0
    );
  const taxAmount = invoice.taxAmount || 0;
  const totalAmount =
    invoice.totalAmount || invoice.amount || subtotal + taxAmount;
  const amountPaid =
    invoice.payments?.reduce((sum, payment) => sum + payment.amountPaid, 0) ||
    0;
  const balanceDue = totalAmount - amountPaid;

  return (
    <div
      className={`fixed inset-0 z-50 overflow-y-auto ${
        isOpen ? "block" : "hidden"
      }`}
    >
      <div className="flex items-center justify-center min-h-screen p-2 lg:p-4">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>

        {/* Modal panel - Full width on desktop */}
        <div className="relative w-full max-w-[95vw] lg:max-w-[90vw] xl:max-w-[85vw] bg-white rounded-lg shadow-xl">
          {/* Modal Header */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center p-6 xl:p-8 bg-gray-50 border-b border-gray-200 gap-4">
            <h2 className="text-2xl xl:text-3xl font-bold text-gray-900">
              Invoice Details
            </h2>
            <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
              <button
                onClick={() => onDownload(invoice)}
                className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium text-base"
              >
                Download PDF
              </button>
              <button
                onClick={onClose}
                className="px-6 py-3 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-base"
              >
                Close
              </button>
            </div>
          </div>

          {/* Invoice Content */}
          <div
            id="invoice-content"
            className="p-6 xl:p-10 max-h-[75vh] xl:max-h-[85vh] overflow-y-auto bg-white"
          >
            {/* Header Section - Company Logo and Invoice Title */}
            <div className="flex justify-between items-start mb-8 border-b-2 border-blue-600 pb-6">
              {/* Company Logo and Name */}
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">EN</span>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-blue-600">
                    {companyInfo.name}
                  </h1>
                  <p className="text-sm text-gray-600">
                    Empowering Enterprises
                  </p>
                </div>
              </div>

              {/* Invoice Title and Number */}
              <div className="text-right">
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  Sales Invoice {invoiceNumber}
                </h2>
                <div className="text-sm text-gray-600 space-y-1">
                  <div>
                    <strong>Invoice Date:</strong>{" "}
                    {format(invoiceDate, "dd/MM/yyyy")}
                  </div>
                  <div>
                    <strong>Due Date:</strong> {format(dueDate, "dd/MM/yyyy")}
                  </div>
                  <div>
                    <strong>Customer Code:</strong> CUST{customer.id || "000"}
                  </div>
                  <div>
                    <strong>Purchase Order:</strong>{" "}
                    {invoice.order?.poNumber || "N/A"}
                  </div>
                  <div>
                    <strong>Delivery Order No.:</strong>{" "}
                    {invoice.order?.deliveryOrderNumber ||
                      invoice.deliveryOrderNumber ||
                      "N/A"}
                  </div>
                </div>
              </div>
            </div>

            {/* From and To Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* From Section */}
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3">From</h3>
                <div className="border border-gray-300 p-4 bg-gray-50">
                  <div className="space-y-2 text-sm">
                    <div className="font-bold text-blue-600">
                      {companyInfo.name}
                    </div>
                    <div>{companyInfo.address}</div>
                    <div>
                      {companyInfo.city}, {companyInfo.state}{" "}
                      {companyInfo.zipCode}
                    </div>
                    <div>{companyInfo.country}</div>
                    <div className="mt-3">
                      <div>
                        <strong>GSTIN:</strong> {companyInfo.gstin}
                      </div>
                      <div>
                        <strong>Phone:</strong> {companyInfo.phone}
                      </div>
                      <div>
                        <strong>Email:</strong> {companyInfo.email}
                      </div>
                      <div>
                        <strong>Website:</strong> {companyInfo.website}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* To Section */}
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3">To</h3>
                <div className="border border-gray-300 p-4">
                  <div className="space-y-2 text-sm">
                    <div className="font-bold">
                      {customer.name || customer.companyName || "N/A"}
                    </div>
                    {customer.businessName &&
                      customer.businessName !==
                        (customer.name || customer.companyName) && (
                        <div>{customer.businessName}</div>
                      )}
                    {customer.address && <div>{customer.address}</div>}
                    {customer.billAddressLine && (
                      <div>{customer.billAddressLine}</div>
                    )}
                    <div>
                      {customer.city || customer.billDistrict || ""}{" "}
                      {customer.state || customer.billState || ""}{" "}
                      {customer.zipCode || customer.billPinCode || ""}
                    </div>
                    <div>
                      {customer.country || customer.billCountry || "India"}
                    </div>
                    <div className="mt-3">
                      {customer.phone && (
                        <div>
                          <strong>Tel:</strong> {customer.phone}
                        </div>
                      )}
                      {customer.contactMobile &&
                        customer.contactMobile !== customer.phone && (
                          <div>
                            <strong>Mobile:</strong> {customer.contactMobile}
                          </div>
                        )}
                      {customer.email && (
                        <div>
                          <strong>Email:</strong> {customer.email}
                        </div>
                      )}
                      {customer.website && (
                        <div>
                          <strong>Website:</strong> {customer.website}
                        </div>
                      )}
                    </div>
                    {customer.gstin && (
                      <div className="mt-3">
                        <div>
                          <strong>GSTIN:</strong> {customer.gstin}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div className="mb-8">
              <div className="border border-gray-300">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-100 border-b border-gray-300">
                      <th className="border-r border-gray-300 px-3 py-3 text-center text-sm font-bold w-12">
                        #
                      </th>
                      <th className="border-r border-gray-300 px-4 py-3 text-left text-sm font-bold">
                        Description
                      </th>
                      <th className="border-r border-gray-300 px-3 py-3 text-center text-sm font-bold w-16">
                        GST%
                      </th>
                      <th className="border-r border-gray-300 px-3 py-3 text-right text-sm font-bold w-24">
                        U.P. (excl)
                      </th>
                      <th className="border-r border-gray-300 px-3 py-3 text-center text-sm font-bold w-16">
                        Qty
                      </th>
                      <th className="border-r border-gray-300 px-3 py-3 text-right text-sm font-bold w-24">
                        GST Amt
                      </th>
                      <th className="border-r border-gray-300 px-3 py-3 text-right text-sm font-bold w-28">
                        Total (excl.)
                      </th>
                      <th className="px-3 py-3 text-center text-sm font-bold w-20">
                        Ack.
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderItems.map((item, index) => {
                      // Handle both order items and invoice items
                      const unitPrice = item.unitPrice || item.price || 0;
                      const quantity = item.quantity || 1;
                      const lineTotal = item.totalPrice || unitPrice * quantity;
                      const gstRate =
                        item.gstPercent || item.product?.gstPercent || 18;
                      const gstAmount =
                        item.gstAmount || (lineTotal * gstRate) / 100;

                      return (
                        <tr key={index} className="border-b border-gray-200">
                          <td className="border-r border-gray-300 px-3 py-3 text-center text-sm font-medium">
                            {index + 1}
                          </td>
                          <td className="border-r border-gray-300 px-4 py-3 text-sm">
                            <div className="font-medium">
                              {item.productSku?.product?.name ||
                                item.productSku?.name ||
                                item.product?.name ||
                                item.productSku?.product?.description ||
                                item.productSku?.description ||
                                item.product?.description ||
                                item.description ||
                                `Product ${item.productId}`}
                            </div>
                            {item.product?.description && (
                              <div className="text-xs text-gray-600 mt-1">
                                {item.product.description}
                              </div>
                            )}
                            {item.product?.sku && (
                              <div className="text-xs text-gray-500 mt-1">
                                SKU: {item.product.sku}
                              </div>
                            )}
                            {item.product?.hsnCode && (
                              <div className="text-xs text-gray-500 mt-1">
                                HSN: {item.product.hsnCode}
                              </div>
                            )}
                          </td>
                          <td className="border-r border-gray-300 px-3 py-3 text-center text-sm">
                            {gstRate}%
                          </td>
                          <td className="border-r border-gray-300 px-3 py-3 text-right text-sm">
                            ₹{unitPrice.toFixed(2)}
                          </td>
                          <td className="border-r border-gray-300 px-3 py-3 text-center text-sm">
                            {quantity}
                          </td>
                          <td className="border-r border-gray-300 px-3 py-3 text-right text-sm">
                            ₹{gstAmount.toFixed(2)}
                          </td>
                          <td className="border-r border-gray-300 px-3 py-3 text-right text-sm font-medium">
                            ₹{lineTotal.toFixed(2)}
                          </td>
                          <td className="px-3 py-3 text-center text-sm">
                            <div className="w-4 h-4 border border-gray-400 mx-auto"></div>
                          </td>
                        </tr>
                      );
                    })}
                    {/* Empty rows for spacing */}
                    {Array.from({
                      length: Math.max(0, 6 - orderItems.length),
                    }).map((_, index) => (
                      <tr
                        key={`empty-${index}`}
                        className="border-b border-gray-200"
                      >
                        <td className="border-r border-gray-300 px-3 py-4">
                          &nbsp;
                        </td>
                        <td className="border-r border-gray-300 px-4 py-4">
                          &nbsp;
                        </td>
                        <td className="border-r border-gray-300 px-3 py-4">
                          &nbsp;
                        </td>
                        <td className="border-r border-gray-300 px-3 py-4">
                          &nbsp;
                        </td>
                        <td className="border-r border-gray-300 px-3 py-4">
                          &nbsp;
                        </td>
                        <td className="border-r border-gray-300 px-3 py-4">
                          &nbsp;
                        </td>
                        <td className="border-r border-gray-300 px-3 py-4">
                          &nbsp;
                        </td>
                        <td className="px-3 py-4">&nbsp;</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Bottom Section - Payment Terms and Totals */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Payment Terms */}
              <div>
                <h4 className="text-sm font-bold text-gray-900 mb-3">
                  Payment Terms:
                </h4>
                <div className="text-sm text-gray-700 space-y-2">
                  <div>
                    <strong>Due:</strong>{" "}
                    {invoice.paymentTerms || "Due Upon Receipt"}
                  </div>
                  <div className="mt-4">
                    <div className="font-semibold mb-2">
                      Payment by transfer to the following bank account:
                    </div>
                    <div>
                      <strong>Account Holder:</strong>{" "}
                      {companyInfo.bankDetails?.accountName ||
                        "Axia B2B Solutions"}
                    </div>
                    <div>
                      <strong>Account Number:</strong>{" "}
                      {companyInfo.bankDetails?.accountNumber ||
                        "1234567890123456"}
                    </div>
                    <div>
                      <strong>Bank:</strong>{" "}
                      {companyInfo.bankDetails?.bankName || "HDFC Bank"}
                    </div>
                    <div>
                      <strong>IFSC Code:</strong>{" "}
                      {companyInfo.bankDetails?.ifscCode || "HDFC0001234"}
                    </div>
                    <div>
                      <strong>Branch:</strong>{" "}
                      {companyInfo.bankDetails?.branch || "Tech City Branch"}
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="font-semibold mb-2">
                      UPI Payment Options:
                    </div>
                    <div>
                      <strong>UPI ID:</strong> ennovaceb2b@hdfc
                    </div>
                    <div>
                      <strong>PayTM/Google Pay:</strong> +91 98765 43210
                    </div>
                  </div>
                </div>
              </div>

              {/* Totals */}
              <div className="text-right">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Total (excl. tax)</span>
                    <span className="font-medium">₹{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total GST 18%</span>
                    <span className="font-medium">₹{taxAmount.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-gray-300 pt-2">
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total (incl. GST)</span>
                      <span>₹{totalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                  {amountPaid > 0 && (
                    <>
                      <div className="flex justify-between text-green-600 mt-3">
                        <span>Amount Paid</span>
                        <span className="font-medium">
                          ₹{amountPaid.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-lg font-bold border-t border-gray-300 pt-2">
                        <span
                          className={
                            balanceDue > 0 ? "text-red-600" : "text-green-600"
                          }
                        >
                          Balance Due
                        </span>
                        <span
                          className={
                            balanceDue > 0 ? "text-red-600" : "text-green-600"
                          }
                        >
                          ₹{balanceDue.toFixed(2)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Salesman Information */}
            {(invoice.order?.salesRepresentative ||
              customer.salesPerson ||
              customer.assignedSalesPerson) && (
              <div className="border-t border-gray-200 pt-6 mb-6">
                <h4 className="text-sm font-bold text-gray-900 mb-3">
                  Sales Representative:
                </h4>
                <div className="text-sm text-gray-700 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <strong>Name:</strong>{" "}
                    {invoice.order?.salesRepresentative ||
                      customer.salesPerson ||
                      customer.assignedSalesPerson}
                  </div>
                  <div>
                    <strong>ID:</strong> {invoice.order?.salesRepId || "N/A"}
                  </div>
                  <div>
                    <strong>Phone:</strong>{" "}
                    {invoice.order?.salesRepPhone || "N/A"}
                  </div>
                  <div>
                    <strong>Email:</strong>{" "}
                    {invoice.order?.salesRepEmail || "N/A"}
                  </div>
                </div>
              </div>
            )}

            {/* Payment History */}
            {invoice.payments && invoice.payments.length > 0 && (
              <div className="border-t border-gray-200 pt-6 mb-6">
                <h4 className="text-sm font-bold text-gray-900 mb-3">
                  Payment History:
                </h4>
                <div className="border border-gray-300">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border-r border-gray-300 px-4 py-2 text-left text-sm font-bold">
                          Date
                        </th>
                        <th className="border-r border-gray-300 px-4 py-2 text-left text-sm font-bold">
                          Method
                        </th>
                        <th className="border-r border-gray-300 px-4 py-2 text-left text-sm font-bold">
                          Reference
                        </th>
                        <th className="px-4 py-2 text-right text-sm font-bold">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.payments.map((payment, index) => (
                        <tr key={index} className="border-b border-gray-200">
                          <td className="border-r border-gray-300 px-4 py-2 text-sm">
                            {format(
                              new Date(payment.paymentDate),
                              "dd/MM/yyyy"
                            )}
                          </td>
                          <td className="border-r border-gray-300 px-4 py-2 text-sm">
                            {payment.method || "N/A"}
                          </td>
                          <td className="border-r border-gray-300 px-4 py-2 text-sm">
                            {payment.transactionId ||
                              payment.paymentReference ||
                              "N/A"}
                          </td>
                          <td className="px-4 py-2 text-sm text-right font-medium">
                            ₹{payment.amountPaid.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Notes */}
            {invoice.notes && (
              <div className="border-t border-gray-200 pt-6 mb-6">
                <h4 className="text-sm font-bold text-gray-900 mb-3">Notes:</h4>
                <div className="text-sm text-gray-700 bg-yellow-50 p-4 border-l-4 border-yellow-400">
                  {invoice.notes}
                </div>
              </div>
            )}

            {/* Terms & Conditions */}
            <div className="border-t border-gray-200 pt-6 mb-6">
              <h4 className="text-sm font-bold text-gray-900 mb-3">
                Terms & Conditions:
              </h4>
              <div className="text-xs text-gray-700 space-y-2">
                <p>
                  1. Payment is due within {invoice.paymentTerms || "30 days"}{" "}
                  from the invoice date.
                </p>
                <p>
                  2. Interest @ 24% per annum will be charged on overdue amounts
                  beyond due date.
                </p>
                <p>
                  3. All disputes are subject to the jurisdiction of courts in{" "}
                  {companyInfo.city} only.
                </p>
                <p>
                  4. Goods once sold will not be taken back or exchanged without
                  prior written approval.
                </p>
                <p>
                  5. Any claims regarding quantity or quality must be reported
                  within 7 days of receipt.
                </p>
                <p>
                  6. Delivery charges are extra unless specifically mentioned
                  otherwise.
                </p>
                <p>
                  7. Risk and title of goods pass to the buyer upon delivery.
                </p>
                <p>
                  8. Force majeure conditions will excuse performance delays.
                </p>
                <p>
                  9. This invoice is subject to our standard terms and
                  conditions of sale.
                </p>
                <p>10. E. & O.E. (Errors and Omissions Excepted)</p>
              </div>
            </div>

            {/* Footer Legal Text */}
            <div className="border-t border-gray-200 pt-6 text-xs text-gray-600">
              <p className="mb-2">
                <strong>This is a computer-generated invoice</strong> and does
                not require a physical signature. The products/services
                mentioned herein constitute a warranty unless specified
                otherwise. No return or exchange will be entertained without
                prior authorization. Thank you for your valuable business.
              </p>
              <div className="flex justify-between items-center">
                <span>GSTIN: {companyInfo.gstin}</span>
                <span>Page 1 of 1</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Pagination and Sorting State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentInvoice, setSelectedPaymentInvoice] = useState(null);

  // Check if any filters are active
  const hasActiveFilters = statusFilter || dateFilter;

  // Fetch invoices from API
  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await apiService.getInvoices();
      setInvoices(response);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error("Failed to fetch invoices");
    } finally {
      setLoading(false);
    }
  };

  // Filter invoices based on search and filters
  const filteredInvoices = invoices.filter((invoice) => {
    const matchesSearch =
      !searchTerm ||
      invoice.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.order?.customer?.name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      invoice.order?.customer?.email
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesStatus = !statusFilter || invoice.status === statusFilter;

    const matchesDate =
      !dateFilter ||
      (invoice.invoiceDate && invoice.invoiceDate.startsWith(dateFilter));

    return matchesSearch && matchesStatus && matchesDate;
  });

  // Sorting Logic
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedInvoices = useMemo(() => {
    let sortableItems = [...filteredInvoices];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle nested properties
        if (sortConfig.key.includes(".")) {
          const keys = sortConfig.key.split(".");
          aValue = keys.reduce(
            (obj, key) => (obj && obj[key] !== "undefined" ? obj[key] : ""),
            a
          );
          bValue = keys.reduce(
            (obj, key) => (obj && obj[key] !== "undefined" ? obj[key] : ""),
            b
          );
        }

        // Handle null/undefined values
        if (aValue === null || aValue === undefined) aValue = "";
        if (bValue === null || bValue === undefined) bValue = "";

        // String comparison
        if (typeof aValue === "string") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredInvoices, sortConfig]);

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedInvoices = sortedInvoices.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(sortedInvoices.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateFilter]);

  // Calculate summary statistics
  const summaryStats = invoices.reduce(
    (acc, invoice) => {
      if (invoice.status === "unpaid" || invoice.status === "partially_paid") {
        acc.outstanding += invoice.totalAmount || invoice.amount || 0;
      }
      if (
        invoice.status === "unpaid" &&
        invoice.dueDate &&
        new Date(invoice.dueDate) < new Date()
      ) {
        acc.overdue += 1;
      }
      if (
        invoice.status === "paid" &&
        invoice.paidAt &&
        new Date(invoice.paidAt).getMonth() === new Date().getMonth()
      ) {
        acc.monthlyCollection += invoice.totalAmount || invoice.amount || 0;
      }
      return acc;
    },
    { outstanding: 0, overdue: 0, monthlyCollection: 0 }
  );

  const getStatusBadge = (status) => {
    const styles = {
      unpaid: {
        background: "#fee2e2",
        color: "#991b1b",
        border: "1px solid #fecaca",
      },
      partially_paid: {
        background: "#fef3c7",
        color: "#92400e",
        border: "1px solid #fde68a",
      },
      paid: {
        background: "#dcfce7",
        color: "#166534",
        border: "1px solid #bbf7d0",
      },
      overdue: {
        background: "#fef2f2",
        color: "#7f1d1d",
        border: "1px solid #fca5a5",
      },
      cancelled: {
        background: "#f3f4f6",
        color: "#374151",
        border: "1px solid #d1d5db",
      },
    };

    const labels = {
      unpaid: "Unpaid",
      partially_paid: "Partially Paid",
      paid: "Paid",
      overdue: "Overdue",
      cancelled: "Cancelled",
    };

    return (
      <span
        style={{
          padding: "4px 12px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: "500",
          ...styles[status],
        }}
      >
        {labels[status] || status}
      </span>
    );
  };

  const getPaymentProgress = (invoice) => {
    if (invoice.status === "paid") return 100;
    if (invoice.status === "partially_paid" && invoice.payments) {
      const totalPaid = invoice.payments.reduce(
        (sum, payment) => sum + payment.amountPaid,
        0
      );
      const totalAmount = invoice.totalAmount || invoice.amount || 0;
      return totalAmount > 0 ? (totalPaid / totalAmount) * 100 : 0;
    }
    return 0;
  };

  const handleViewInvoice = async (invoice) => {
    try {
      setLoadingInvoice(true);
      const fullInvoice = await apiService.getInvoice(invoice.id);
      setSelectedInvoice(fullInvoice);
      setShowInvoiceModal(true);
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      toast.error("Failed to load invoice details");
    } finally {
      setLoadingInvoice(false);
    }
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      setDownloadingPDF(invoice.id);

      // Get full invoice data if not already loaded
      const fullInvoice =
        selectedInvoice || (await apiService.getInvoice(invoice.id));

      // Generate PDF using the professional HTML template
      const pdfContent = generateProfessionalInvoiceHTML(fullInvoice);

      // Generate PDF with A4 portrait layout
      const options = {
        margin: [8, 8, 12, 8], // Top, Left, Bottom, Right (mm) - more compact
        filename: `${
          fullInvoice.invoiceNumber || `Invoice-${fullInvoice.id}`
        }.pdf`,
        image: {
          type: "jpeg",
          quality: 0.98,
        },
        html2canvas: {
          scale: 2,
          useCORS: true,
          letterRendering: true,
          logging: false,
          width: 794, // A4 width in pixels at 96 DPI
          height: 1123, // A4 height in pixels at 96 DPI
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
          compress: true,
        },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };

      // Create element and generate PDF with A4 layout
      const element = document.createElement("div");
      element.innerHTML = pdfContent;
      element.style.width = "190mm"; // A4 width with margins
      element.style.fontFamily = "Arial, Helvetica, sans-serif";
      element.style.fontSize = "9px"; // Smaller font size
      element.style.lineHeight = "1.2"; // Tighter line height
      element.style.backgroundColor = "white";

      await html2pdf().set(options).from(element).save();

      toast.success("Invoice PDF downloaded successfully");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Failed to download PDF");
    } finally {
      setDownloadingPDF(null);
    }
  };

  const generateProfessionalInvoiceHTML = (invoice) => {
    const companyInfo = {
      name: "Axia B2B Solutions",
      address: "123 Business Park, Suite 100",
      city: "Tech City, State 12345",
      state: "State",
      zipCode: "12345",
      country: "India",
      phone: "+91 98765 43210",
      email: "sales@ennovaceb2b.com",
      website: "www.ennovaceb2b.com",
      gstin: "27ABCDE1234F1Z5",
      pan: "ABCDE1234F",
      logo: "/logo192.png",
      bankDetails: {
        bankName: "HDFC Bank",
        accountName: "Axia B2B Solutions",
        accountNumber: "1234567890123456",
        ifscCode: "HDFC0001234",
        branch: "Tech City Branch",
        accountType: "Current Account",
      },
    };

    const customer = invoice.order?.customer || {};
    const orderItems = invoice.items || invoice.order?.orderItems || [];
    const invoiceNumber = invoice.invoiceNumber || `INV-${invoice.id}`;
    const invoiceDate = invoice.invoiceDate
      ? new Date(invoice.invoiceDate)
      : new Date(invoice.createdAt);
    const dueDate = new Date(invoice.dueDate);

    // Calculate totals from line items
    let subtotal = 0;
    let totalGST = 0;

    orderItems.forEach((item) => {
      const unitPrice = item.unitPrice || item.price || 0;
      const quantity = item.quantity || 1;
      const lineTotal = item.totalPrice || unitPrice * quantity;
      const gstRate = item.gstPercent || item.product?.gstPercent || 18;
      const gstAmount = item.gstAmount || (lineTotal * gstRate) / 100;

      subtotal += lineTotal;
      totalGST += gstAmount;
    });

    // Use calculated values or fallback to invoice values
    subtotal = subtotal || invoice.subtotal || 0;
    totalGST = totalGST || invoice.taxAmount || 0;
    const totalAmount =
      invoice.totalAmount || invoice.amount || subtotal + totalGST;

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Invoice ${invoiceNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Arial', 'Helvetica', sans-serif; color: #333; line-height: 1.2; margin: 0; padding: 0; background: white; }
          .container { width: 190mm; margin: 0 auto; padding: 8mm; }
          .page { min-height: 257mm; position: relative; page-break-after: auto; }
          .page-break { page-break-before: always; }
          .no-break { page-break-inside: avoid; }
          
          /* Header */
          .header { border-bottom: 2px solid #2563eb; padding-bottom: 8px; margin-bottom: 10px; display: flex; justify-content: space-between; align-items: flex-start; page-break-inside: avoid; }
          .company-logo { width: 28px; height: 28px; background: #2563eb; border-radius: 3px; display: flex; align-items: center; justify-content: center; margin-right: 6px; float: left; }
          .company-logo span { color: white; font-size: 12px; font-weight: bold; }
          .company-section { display: flex; align-items: center; }
          .company-name { color: #2563eb; font-size: 14px; font-weight: bold; margin-bottom: 1px; }
          .company-tagline { font-size: 7px; color: #666; }
          .invoice-section { text-align: right; }
          .invoice-title { font-size: 12px; font-weight: bold; color: #1f2937; margin-bottom: 6px; }
          .invoice-details { font-size: 8px; color: #666; }
          .invoice-details div { margin-bottom: 1px; }
          
          /* From/To sections */
          .from-to-section { display: grid; grid-template-columns: 1fr 1fr; gap: 12mm; margin-bottom: 10mm; page-break-inside: avoid; }
          .section-header { font-size: 9px; font-weight: bold; color: #1f2937; margin-bottom: 3px; }
          .from-box, .to-box { border: 1px solid #d1d5db; padding: 6px; }
          .from-box { background: #f9fafb; }
          .company-details, .customer-details { font-size: 8px; line-height: 1.2; }
          .company-details .company-name-inner { color: #2563eb; font-weight: bold; margin-bottom: 2px; }
          .customer-details .customer-name { font-weight: bold; margin-bottom: 2px; }
          
          /* Items Table */
          .items-table { width: 100%; border-collapse: collapse; margin: 10mm 0; border: 1px solid #d1d5db; page-break-inside: auto; }
          .items-table th { background: #f9fafb; border: 1px solid #d1d5db; padding: 3px 2px; font-size: 7px; font-weight: bold; color: #374151; }
          .items-table td { border: 1px solid #d1d5db; padding: 3px 2px; font-size: 6px; }
          .item-name { font-weight: 500; font-size: 6px; }
          .item-desc { color: #666; font-size: 5px; margin-top: 1px; }
          .item-sku { color: #999; font-size: 5px; margin-top: 1px; }
          .ack-box { width: 10px; height: 10px; border: 1px solid #333; display: inline-block; }
          
          /* Payment Terms and Totals */
          .bottom-section { display: grid; grid-template-columns: 1fr 1fr; gap: 12mm; margin: 10mm 0; page-break-inside: avoid; }
          .payment-terms { font-size: 8px; }
          .payment-terms h4 { font-size: 9px; font-weight: bold; margin-bottom: 4px; }
          .bank-details, .upi-details { margin-top: 6px; }
          .bank-details h5, .upi-details h5 { font-size: 8px; font-weight: bold; margin-bottom: 2px; }
          
          .totals-section { text-align: right; }
          .total-row { display: flex; justify-content: space-between; padding: 1px 0; font-size: 8px; }
          .total-row.final { border-top: 1px solid #d1d5db; padding-top: 3px; margin-top: 3px; font-size: 10px; font-weight: bold; }
          
          /* Salesman */
          .salesman-section { border-top: 1px solid #e5e7eb; padding-top: 6px; margin: 6px 0; page-break-inside: avoid; }
          .salesman-section h4 { font-size: 9px; font-weight: bold; margin-bottom: 3px; }
          .salesman-grid { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 6px; font-size: 7px; }
          
          /* Terms */
          .terms-section { border-top: 1px solid #e5e7eb; padding-top: 6px; margin: 6px 0; page-break-inside: avoid; }
          .terms-section h4 { font-size: 9px; font-weight: bold; margin-bottom: 3px; }
          .terms-list { font-size: 6px; color: #555; line-height: 1.2; }
          .terms-list p { margin-bottom: 1px; }
          
          /* Notes */
          .notes-section { background: #fef3c7; padding: 4px; border-left: 3px solid #f59e0b; margin: 6px 0; page-break-inside: avoid; }
          .notes-section h4 { font-size: 9px; font-weight: bold; color: #92400e; margin-bottom: 2px; }
          .notes-content { font-size: 7px; color: #92400e; }
          
          /* Footer */
          .footer-legal { border-top: 1px solid #e5e7eb; padding-top: 6px; margin-top: 10px; font-size: 6px; color: #666; page-break-inside: avoid; }
          .footer-bottom { display: flex; justify-content: space-between; margin-top: 3px; }
          
          /* Page Header - repeated on each page */
          .page-header { 
            position: running(header); 
            top: 0; 
            left: 0; 
            right: 0; 
            background: white; 
            border-bottom: 1px solid #e5e7eb; 
            padding: 4mm 8mm; 
            z-index: 1000; 
            page-break-after: avoid;
          }
          
          .page-footer { 
            position: running(footer); 
            bottom: 8mm; 
            left: 0; 
            right: 0; 
            text-align: center; 
            font-size: 7px; 
            color: #666; 
            background: white;
            padding: 2mm 0;
            border-top: 1px solid #e5e7eb;
          }
          
          /* Page Number */
          .page-number { 
            font-weight: bold; 
            color: #374151; 
          }
          
          /* Content area with header/footer spacing */
          .content-area { 
            margin-top: 25mm; 
            margin-bottom: 20mm; 
            min-height: calc(257mm - 45mm); 
          }
          
          /* Ensure proper page breaks */
          .page-break-before { page-break-before: always; }
          .page-break-after { page-break-after: always; }
          .page-break-inside-avoid { page-break-inside: avoid; }
          
          /* Page setup for proper headers/footers */
          @page {
            size: A4;
            margin: 15mm 10mm 20mm 10mm;
            @top-center { content: element(header); }
            @bottom-center { content: element(footer); }
          }
          
          @media print { 
            .page-header { 
              position: running(header); 
              top: 0; 
            }
            .page-footer { 
              position: running(footer); 
              bottom: 0; 
            }
            .content-area { 
              margin-top: 25mm; 
              margin-bottom: 20mm; 
            }
            body { 
              margin: 0; 
              padding: 0; 
            }
            .page { page-break-after: auto; }
            .items-table { page-break-inside: auto; }
          }
          .clearfix::after { content: ""; display: table; clear: both; }
        </style>
      </head>
      <body>
        <!-- Page Header - Fixed on all pages -->
        <div class="page-header">
          <div style="display: flex; justify-content: space-between; align-items: flex-start;">
            <!-- Left side: Company/Customer details -->
            <div style="flex: 1;">
              <div style="display: flex; align-items: center; margin-bottom: 2px;">
                <div style="width: 20px; height: 20px; background: #2563eb; border-radius: 2px; display: flex; align-items: center; justify-content: center; margin-right: 4px;">
                  <span style="color: white; font-size: 8px; font-weight: bold;">EN</span>
                </div>
                <div style="color: #2563eb; font-size: 10px; font-weight: bold;">${
                  companyInfo.name
                }</div>
              </div>
              <div style="font-size: 6px; color: #666; line-height: 1.2;">
                <div>${companyInfo.address}</div>
                <div>${companyInfo.city}, ${companyInfo.country}</div>
                <div>Phone: ${companyInfo.phone} | Email: ${
      companyInfo.email
    }</div>
                <div>GSTIN: ${companyInfo.gstin} | PAN: ${companyInfo.pan}</div>
              </div>
            </div>
            
            <!-- Right side: Document information -->
            <div style="text-align: right; flex: 1;">
              <div style="font-size: 12px; font-weight: bold; color: #1f2937; margin-bottom: 4px;">SALES INVOICE</div>
              <div style="font-size: 8px; color: #666; line-height: 1.3;">
                <div><strong>Document No:</strong> ${invoiceNumber}</div>
                <div><strong>Date:</strong> ${format(
                  invoiceDate,
                  "dd/MM/yyyy"
                )}</div>
                <div><strong>Customer:</strong> ${
                  customer.name || customer.companyName || "N/A"
                }</div>
                <div><strong>Customer Code:</strong> CUST${
                  customer.id || "000"
                }</div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Page Footer - Fixed on all pages -->
        <div class="page-footer">
          <div style="display: flex; justify-content: space-between; align-items: center; font-size: 6px;">
            <div>Generated by ${companyInfo.name}</div>
            <div style="font-weight: bold; color: #374151;">Page <span class="page-number"></span></div>
            <div>${format(new Date(), "dd/MM/yyyy HH:mm")}</div>
          </div>
        </div>
        
        <div class="container">
          <div class="content-area">
            <!-- Content starts directly since header is now fixed -->

            <!-- From and To Section -->
            <div class="from-to-section">
              <div>
                <div class="section-header">From</div>
                <div class="from-box">
                  <div class="company-details">
                    <div class="company-name-inner">${companyInfo.name}</div>
                    <div>${companyInfo.address}</div>
                    <div>${companyInfo.city}, ${companyInfo.state} ${
      companyInfo.zipCode
    }</div>
                    <div>${companyInfo.country}</div>
                    <br>
                    <div><strong>GSTIN:</strong> ${companyInfo.gstin}</div>
                    <div><strong>Phone:</strong> ${companyInfo.phone}</div>
                    <div><strong>Email:</strong> ${companyInfo.email}</div>
                    <div><strong>Website:</strong> ${companyInfo.website}</div>
                  </div>
                </div>
              </div>
              <div>
                <div class="section-header">To</div>
                <div class="to-box">
                  <div class="customer-details">
                    <div class="customer-name">${
                      customer.name || customer.companyName || "N/A"
                    }</div>
                    ${
                      customer.businessName &&
                      customer.businessName !==
                        (customer.name || customer.companyName)
                        ? `<div>${customer.businessName}</div>`
                        : ""
                    }
                    ${customer.address ? `<div>${customer.address}</div>` : ""}
                    ${
                      customer.billAddressLine
                        ? `<div>${customer.billAddressLine}</div>`
                        : ""
                    }
                    <div>${customer.city || customer.billDistrict || ""} ${
      customer.state || customer.billState || ""
    } ${customer.zipCode || customer.billPinCode || ""}</div>
                    <div>${
                      customer.country || customer.billCountry || "India"
                    }</div>
                    <br>
                    ${
                      customer.phone
                        ? `<div><strong>Tel:</strong> ${customer.phone}</div>`
                        : ""
                    }
                    ${
                      customer.contactMobile &&
                      customer.contactMobile !== customer.phone
                        ? `<div><strong>Mobile:</strong> ${customer.contactMobile}</div>`
                        : ""
                    }
                    ${
                      customer.email
                        ? `<div><strong>Email:</strong> ${customer.email}</div>`
                        : ""
                    }
                    ${
                      customer.website
                        ? `<div><strong>Website:</strong> ${customer.website}</div>`
                        : ""
                    }
                    ${
                      customer.gstin
                        ? `<br><div><strong>GSTIN:</strong> ${customer.gstin}</div>`
                        : ""
                    }
                  </div>
                </div>
              </div>
            </div>

            <!-- Items Table -->
            <table class="items-table">
              <thead>
                <tr>
                  <th style="width: 30px; text-align: center;">#</th>
                  <th style="text-align: left;">Description</th>
                  <th style="width: 40px; text-align: center;">GST%</th>
                  <th style="width: 60px; text-align: right;">U.P. (excl)</th>
                  <th style="width: 30px; text-align: center;">Qty</th>
                  <th style="width: 60px; text-align: right;">GST Amt</th>
                  <th style="width: 70px; text-align: right;">Total (excl.)</th>
                  <th style="width: 40px; text-align: center;">Ack.</th>
                </tr>
              </thead>
              <tbody>
                ${orderItems
                  .map((item, index) => {
                    // Handle both order items and invoice items
                    const unitPrice = item.unitPrice || item.price || 0;
                    const quantity = item.quantity || 1;
                    const lineTotal = item.totalPrice || unitPrice * quantity;
                    const gstRate =
                      item.gstPercent || item.product?.gstPercent || 18;
                    const gstAmount =
                      item.gstAmount || (lineTotal * gstRate) / 100;

                    return `
                    <tr>
                      <td style="text-align: center; font-weight: 500;">${
                        index + 1
                      }</td>
                      <td>
                        <div class="item-name">${
                          item.productSku?.product?.name ||
                          item.productSku?.name ||
                          item.product?.name ||
                          item.productSku?.product?.description ||
                          item.productSku?.description ||
                          item.product?.description ||
                          item.description ||
                          `Product ${item.productId}`
                        }</div>
                        ${
                          item.product?.description
                            ? `<div class="item-desc">${item.product.description}</div>`
                            : ""
                        }
                        ${
                          item.product?.sku
                            ? `<div class="item-sku">SKU: ${item.product.sku}</div>`
                            : ""
                        }
                        ${
                          item.product?.hsnCode
                            ? `<div class="item-sku">HSN: ${item.product.hsnCode}</div>`
                            : ""
                        }
                      </td>
                      <td style="text-align: center;">${gstRate}%</td>
                      <td style="text-align: right;">₹${unitPrice.toFixed(
                        2
                      )}</td>
                      <td style="text-align: center;">${quantity}</td>
                      <td style="text-align: right;">₹${gstAmount.toFixed(
                        2
                      )}</td>
                      <td style="text-align: right; font-weight: 500;">₹${lineTotal.toFixed(
                        2
                      )}</td>
                      <td style="text-align: center;"><span class="ack-box"></span></td>
                    </tr>
                  `;
                  })
                  .join("")}
                ${Array.from({ length: Math.max(0, 6 - orderItems.length) })
                  .map(
                    (_, index) => `
                  <tr>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                  </tr>
                `
                  )
                  .join("")}
              </tbody>
            </table>

            <!-- Payment Terms and Totals Section -->
            <div class="bottom-section">
              <!-- Payment Terms -->
              <div class="payment-terms">
                <h4>Payment Terms:</h4>
                <div><strong>Due:</strong> ${
                  invoice.paymentTerms || "Due Upon Receipt"
                }</div>
                
                <div class="bank-details">
                  <h5>Payment by transfer to the following bank account:</h5>
                  <div><strong>Account Holder:</strong> ${
                    companyInfo.bankDetails?.accountName || "Axia B2B Solutions"
                  }</div>
                  <div><strong>Account Number:</strong> ${
                    companyInfo.bankDetails?.accountNumber || "1234567890123456"
                  }</div>
                  <div><strong>Bank:</strong> ${
                    companyInfo.bankDetails?.bankName || "HDFC Bank"
                  }</div>
                  <div><strong>IFSC Code:</strong> ${
                    companyInfo.bankDetails?.ifscCode || "HDFC0001234"
                  }</div>
                  <div><strong>Branch:</strong> ${
                    companyInfo.bankDetails?.branch || "Tech City Branch"
                  }</div>
                </div>
                
                <div class="upi-details">
                  <h5>UPI Payment Options:</h5>
                  <div><strong>UPI ID:</strong> ennovaceb2b@hdfc</div>
                  <div><strong>PayTM/Google Pay:</strong> +91 98765 43210</div>
                </div>
              </div>
              
              <!-- Totals -->
              <div class="totals-section">
                <div class="total-row">
                  <span>Total (excl. tax)</span>
                  <span>₹${subtotal.toFixed(2)}</span>
                </div>
                <div class="total-row">
                  <span>Total GST 18%</span>
                  <span>₹${totalGST.toFixed(2)}</span>
                </div>
                <div class="total-row final">
                  <span>Total (incl. GST)</span>
                  <span>₹${totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>

            ${
              invoice.order?.salesRepresentative ||
              customer.salesPerson ||
              customer.assignedSalesPerson
                ? `
              <!-- Salesman Information -->
              <div class="salesman-section">
                <h4>Sales Representative:</h4>
                <div class="salesman-grid">
                  <div><strong>Name:</strong> ${
                    invoice.order?.salesRepresentative ||
                    customer.salesPerson ||
                    customer.assignedSalesPerson
                  }</div>
                  <div><strong>ID:</strong> ${
                    invoice.order?.salesRepId || "N/A"
                  }</div>
                  <div><strong>Phone:</strong> ${
                    invoice.order?.salesRepPhone || "N/A"
                  }</div>
                  <div><strong>Email:</strong> ${
                    invoice.order?.salesRepEmail || "N/A"
                  }</div>
                </div>
              </div>
            `
                : ""
            }

            ${
              invoice.notes
                ? `
              <div class="notes-section">
                <h4>Notes:</h4>
                <div class="notes-content">${invoice.notes}</div>
              </div>
            `
                : ""
            }

            <!-- Terms & Conditions -->
            <div class="terms-section">
              <h4>Terms & Conditions:</h4>
              <div class="terms-list">
                <p>1. Payment is due within ${
                  invoice.paymentTerms || "30 days"
                } from the invoice date.</p>
                <p>2. Interest @ 24% per annum will be charged on overdue amounts beyond due date.</p>
                <p>3. All disputes are subject to the jurisdiction of courts in ${
                  companyInfo.city
                } only.</p>
                <p>4. Goods once sold will not be taken back or exchanged without prior written approval.</p>
                <p>5. Any claims regarding quantity or quality must be reported within 7 days of receipt.</p>
                <p>6. Delivery charges are extra unless specifically mentioned otherwise.</p>
                <p>7. Risk and title of goods pass to the buyer upon delivery.</p>
                <p>8. Force majeure conditions will excuse performance delays.</p>
                <p>9. This invoice is subject to our standard terms and conditions of sale.</p>
                <p>10. E. & O.E. (Errors and Omissions Excepted)</p>
              </div>
            </div>

            <!-- Footer Legal Text -->
            <div class="footer-legal">
              <p><strong>This is a computer-generated invoice</strong> and does not require a physical signature. The products/services mentioned herein constitute a warranty unless specified otherwise. No return or exchange will be entertained without prior authorization. Thank you for your valuable business.</p>
              <div class="footer-bottom">
                <span>GSTIN: ${companyInfo.gstin}</span>
                <span>Page 1 of 1</span>
              </div>
            </div>
          </div>
          
          <!-- Page Footer with Numbering -->
          <div class="page-footer">
            <div style="display: flex; justify-content: space-between; width: 100%; padding: 0 15mm;">
              <span>Invoice: ${invoiceNumber}</span>
              <span class="page-number"></span>
              <span>Date: ${format(new Date(), "dd/MM/yyyy")}</span>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const handleMarkPayment = async (invoice) => {
    setSelectedPaymentInvoice(invoice);
    setShowPaymentModal(true);
  };

  const handlePaymentSaved = () => {
    // Refresh invoices after payment is saved
    fetchInvoices();
  };

  const handleExportInvoices = async () => {
    try {
      // Generate CSV export
      const csvContent = generateCSVContent(filteredInvoices);
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `invoices-${format(new Date(), "yyyy-MM-dd")}.csv`;
      link.click();
      toast.success("Invoices exported successfully");
    } catch (error) {
      console.error("Error exporting invoices:", error);
      toast.error("Failed to export invoices");
    }
  };

  const generateCSVContent = (invoices) => {
    const headers = [
      "Invoice Number",
      "Customer Name",
      "Email",
      "Invoice Date",
      "Due Date",
      "Amount",
      "Tax Amount",
      "Total Amount",
      "Status",
    ];
    const rows = invoices.map((invoice) => [
      invoice.invoiceNumber || `INV-${invoice.id}`,
      invoice.order?.customer?.name || "N/A",
      invoice.order?.customer?.email || "N/A",
      invoice.invoiceDate
        ? format(new Date(invoice.invoiceDate), "yyyy-MM-dd")
        : format(new Date(invoice.createdAt), "yyyy-MM-dd"),
      format(new Date(invoice.dueDate), "yyyy-MM-dd"),
      invoice.subtotal || 0,
      invoice.taxAmount || 0,
      invoice.totalAmount || invoice.amount || 0,
      invoice.status,
    ]);

    return [headers, ...rows]
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Invoices
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Track and manage customer invoices
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExportInvoices}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={fetchInvoices}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/25 transition-all duration-200"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {invoices.length}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Total Invoices
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 dark:bg-red-900/30 rounded-lg">
              <DollarSign className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                ₹{summaryStats.outstanding.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Outstanding
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {summaryStats.overdue}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Overdue
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
              <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                ₹{summaryStats.monthlyCollection.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                This Month
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              />
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-medium transition-all ${
                showFilters || hasActiveFilters
                  ? "bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-300"
                  : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
              {hasActiveFilters && (
                <span className="ml-1 w-5 h-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
                  {[statusFilter, dateFilter].filter(Boolean).length}
                </span>
              )}
            </button>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="unpaid">Unpaid</option>
                  <option value="partially_paid">Partially Paid</option>
                  <option value="paid">Paid</option>
                  <option value="overdue">Overdue</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                  Invoice Date
                </label>
                <input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>

              <div className="sm:col-span-2 flex items-end">
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setStatusFilter("");
                    setDateFilter("");
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Results count */}
        <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-900/50 flex items-center justify-between text-sm">
          <span className="text-gray-600 dark:text-gray-400">
            Showing{" "}
            <span className="font-semibold text-gray-900 dark:text-white">
              {sortedInvoices.length}
            </span>{" "}
            invoices
          </span>
          <button
            onClick={fetchInvoices}
            className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleSort("invoiceNumber")}
                >
                  <div className="flex items-center gap-1.5">
                    Invoice
                    {sortConfig.key === "invoiceNumber" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp className="w-3.5 h-3.5" />
                      ) : (
                        <ArrowDown className="w-3.5 h-3.5" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3.5 h-3.5 text-gray-300" />
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleSort("order.customer.businessName")}
                >
                  <div className="flex items-center gap-1.5">
                    Customer
                    {sortConfig.key === "order.customer.businessName" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp className="w-3.5 h-3.5" />
                      ) : (
                        <ArrowDown className="w-3.5 h-3.5" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3.5 h-3.5 text-gray-300" />
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors hidden md:table-cell"
                  onClick={() => handleSort("invoiceDate")}
                >
                  <div className="flex items-center gap-1.5">
                    Date
                    {sortConfig.key === "invoiceDate" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp className="w-3.5 h-3.5" />
                      ) : (
                        <ArrowDown className="w-3.5 h-3.5" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3.5 h-3.5 text-gray-300" />
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors hidden lg:table-cell"
                  onClick={() => handleSort("dueDate")}
                >
                  <div className="flex items-center gap-1.5">
                    Due Date
                    {sortConfig.key === "dueDate" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp className="w-3.5 h-3.5" />
                      ) : (
                        <ArrowDown className="w-3.5 h-3.5" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3.5 h-3.5 text-gray-300" />
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleSort("totalAmount")}
                >
                  <div className="flex items-center gap-1.5">
                    Amount
                    {sortConfig.key === "totalAmount" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp className="w-3.5 h-3.5" />
                      ) : (
                        <ArrowDown className="w-3.5 h-3.5" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3.5 h-3.5 text-gray-300" />
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors hidden md:table-cell"
                  onClick={() => handleSort("status")}
                >
                  <div className="flex items-center gap-1.5">
                    Status
                    {sortConfig.key === "status" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp className="w-3.5 h-3.5" />
                      ) : (
                        <ArrowDown className="w-3.5 h-3.5" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3.5 h-3.5 text-gray-300" />
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {paginatedInvoices.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center">
                    <FileText className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400 font-medium">
                      No invoices found
                    </p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                      Try adjusting your search or filters
                    </p>
                  </td>
                </tr>
              ) : (
                paginatedInvoices.map((invoice) => {
                  const totalPaid =
                    invoice.payments?.reduce(
                      (sum, payment) => sum + payment.amountPaid,
                      0
                    ) || 0;

                  return (
                    <tr
                      key={invoice.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                            <FileText className="w-5 h-5" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {invoice.invoiceNumber || `INV-${invoice.id}`}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Order: #{invoice.order?.id || "N/A"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-900 dark:text-white font-medium">
                          {invoice.order?.customer?.businessName ||
                            invoice.order?.customer?.name ||
                            "N/A"}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {invoice.order?.customer?.email || ""}
                        </p>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {invoice.invoiceDate
                            ? format(
                                new Date(invoice.invoiceDate),
                                "MMM dd, yyyy"
                              )
                            : format(
                                new Date(invoice.createdAt),
                                "MMM dd, yyyy"
                              )}
                        </p>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {format(new Date(invoice.dueDate), "MMM dd, yyyy")}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          ₹
                          {(
                            invoice.totalAmount ||
                            invoice.amount ||
                            0
                          ).toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Tax: ₹{(invoice.taxAmount || 0).toLocaleString()}
                        </p>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div>
                          {getStatusBadge(invoice.status)}
                          {invoice.status === "partially_paid" && (
                            <div className="mt-2">
                              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                                <div
                                  className="bg-amber-500 h-1.5 rounded-full transition-all duration-300"
                                  style={{
                                    width: `${getPaymentProgress(invoice)}%`,
                                  }}
                                ></div>
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                ₹{totalPaid?.toLocaleString()} paid
                              </p>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => handleViewInvoice(invoice)}
                            disabled={loadingInvoice}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 rounded-lg transition-colors"
                            title="View Invoice"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">View</span>
                          </button>
                          <button
                            onClick={() => handleDownloadPDF(invoice)}
                            disabled={downloadingPDF === invoice.id}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 dark:text-emerald-400 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/50 rounded-lg transition-colors"
                            title="Download PDF"
                          >
                            <Download className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">
                              {downloadingPDF === invoice.id ? "..." : "PDF"}
                            </span>
                          </button>
                          {invoice.status !== "paid" && (
                            <button
                              onClick={() => handleMarkPayment(invoice)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 dark:text-purple-400 dark:bg-purple-900/30 dark:hover:bg-purple-900/50 rounded-lg transition-colors"
                              title="Record Payment"
                            >
                              <CreditCard className="w-3.5 h-3.5" />
                              <span className="hidden sm:inline">Pay</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400 hidden sm:block">
              Page {currentPage} of {totalPages}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-lg border transition-colors ${
                  currentPage === 1
                    ? "text-gray-300 dark:text-gray-600 border-gray-200 dark:border-gray-700 cursor-not-allowed"
                    : "text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(
                  (num) =>
                    num === 1 ||
                    num === totalPages ||
                    (num >= currentPage - 1 && num <= currentPage + 1)
                )
                .reduce((acc, num, i, arr) => {
                  if (i > 0 && num - arr[i - 1] > 1) {
                    acc.push("...");
                  }
                  acc.push(num);
                  return acc;
                }, [])
                .map((item, index) =>
                  item === "..." ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="px-2 text-gray-400"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={item}
                      onClick={() => paginate(item)}
                      className={`min-w-[36px] h-9 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === item
                          ? "bg-blue-600 text-white"
                          : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                      }`}
                    >
                      {item}
                    </button>
                  )
                )}

              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-lg border transition-colors ${
                  currentPage === totalPages
                    ? "text-gray-300 dark:text-gray-600 border-gray-200 dark:border-gray-700 cursor-not-allowed"
                    : "text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Professional Invoice Modal */}
      <ProfessionalInvoiceModal
        invoice={selectedInvoice}
        isOpen={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
        onDownload={handleDownloadPDF}
      />

      {/* Payment Modal */}
      <PaymentModal
        invoice={selectedPaymentInvoice}
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setSelectedPaymentInvoice(null);
        }}
        onPaymentSaved={handlePaymentSaved}
      />
    </div>
  );
}
